'use client';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type React from 'react';
import { useMemo, useState } from 'react';

export interface TreeNode {
  value: string;
  label: string;
  children?: TreeNode[];
}

interface TreeSelectSectionProps {
  nodes: TreeNode[];
  /** 선택된 잎(주유소) 값 */
  values: string[];
  /** 선택된 잎 값 목록을 넘긴다 */
  onValuesChange: (values: string[]) => void;
  /** 잎 하나만 고를 수 있는 화면 - 상위 노드에는 체크박스를 두지 않는다 */
  singleSelect?: boolean;
  placeholder?: string;
}

export const leavesOf = (node: TreeNode): TreeNode[] =>
  node.children?.length ? node.children.flatMap(leavesOf) : [node];

export function TreeSelectSection({
  nodes,
  values,
  onValuesChange,
  singleSelect = false,
  placeholder = '선택',
}: TreeSelectSectionProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const selected = useMemo(() => new Set(values), [values]);
  const q = query.trim().toLowerCase();
  const matches = (node: TreeNode): boolean =>
    node.label.toLowerCase().includes(q) ||
    (node.children || []).some(matches);

  const leafLabels = useMemo(() => {
    const map = new Map<string, string>();
    nodes.flatMap(leavesOf).forEach(leaf => map.set(leaf.value, leaf.label));
    return map;
  }, [nodes]);

  const label = values.length
    ? `${leafLabels.get(values[0]) ?? values[0]}${values.length > 1 ? ` 외 ${values.length - 1}` : ''}`
    : placeholder;

  const toggleLeaf = (value: string) => {
    if (singleSelect) {
      onValuesChange([value]);
      return;
    }
    onValuesChange(
      selected.has(value)
        ? values.filter(v => v !== value)
        : [...values, value]
    );
  };

  const toggleBranch = (node: TreeNode) => {
    const leaves = leavesOf(node).map(leaf => leaf.value);
    const allOn = leaves.every(v => selected.has(v));
    onValuesChange(
      allOn
        ? values.filter(v => !leaves.includes(v))
        : [...values, ...leaves.filter(v => !selected.has(v))]
    );
  };

  const renderNodes = (list: TreeNode[], depth: number): React.ReactElement[] =>
    list.flatMap(node => {
      if (q && !matches(node)) return [];

      const isBranch = !!node.children?.length;
      const leaves = leavesOf(node).map(leaf => leaf.value);
      const onCount = leaves.filter(v => selected.has(v)).length;
      const allOn = onCount === leaves.length;
      // 검색 중에는 결과가 보이도록 모두 펼친다
      const isOpen = q ? true : !!expanded[node.value];

      return [
        <div
          key={node.value}
          className='flex items-center gap-2 py-1.5 pr-3 text-sm cursor-pointer hover:bg-muted/60 rounded-sm'
          style={{ paddingLeft: 8 + depth * 16 }}
          onClick={() =>
            isBranch
              ? setExpanded(prev => ({ ...prev, [node.value]: !isOpen }))
              : toggleLeaf(node.value)
          }
        >
          <span className='w-4 shrink-0 text-muted-foreground'>
            {isBranch &&
              (isOpen ? (
                <ChevronDown className='size-3.5' />
              ) : (
                <ChevronRight className='size-3.5' />
              ))}
          </span>
          {isBranch && singleSelect ? (
            // 단일 선택 화면에서는 상위 노드를 통째로 고를 수 없다
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className='inline-flex shrink-0'
                  onClick={e => e.stopPropagation()}
                >
                  {/* 비활성 버튼은 포인터 이벤트를 삼켜 툴팁이 안 뜬다 */}
                  <Checkbox
                    checked={false}
                    disabled
                    className='pointer-events-none disabled:border-gray-300 disabled:bg-gray-100 disabled:opacity-100'
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>현재 페이지는 주유소 한 곳만 선택해야 합니다</TooltipContent>
            </Tooltip>
          ) : (
            <Checkbox
              checked={allOn ? true : onCount > 0 ? 'indeterminate' : false}
              onClick={e => {
                e.stopPropagation();
                isBranch ? toggleBranch(node) : toggleLeaf(node.value);
              }}
            />
          )}
          <span className={depth === 0 ? 'font-medium' : undefined}>
            {node.label}
          </span>
          {isBranch && (
            <span className='ml-auto text-xs text-muted-foreground'>
              {onCount}/{leaves.length}
            </span>
          )}
        </div>,
        ...(isBranch && isOpen ? renderNodes(node.children!, depth + 1) : []),
      ];
    });

  const rows = renderNodes(nodes, 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          size='sm'
          className='w-[200px] justify-between font-normal'
        >
          <span className='truncate'>{label}</span>
          <ChevronDown className='size-3.5 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[280px] p-0' align='start'>
        <div className='p-2'>
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder='검색'
            inputSize='sm'
          />
        </div>
        {rows.length === 0 ? (
          <div className='px-3 py-4 text-sm text-muted-foreground'>
            {q ? `'${query}' 검색 결과 없음` : '선택할 항목이 없습니다'}
          </div>
        ) : (
          <TooltipProvider delayDuration={200}>
            <div className='max-h-[260px] overflow-auto px-1 pb-1'>{rows}</div>
          </TooltipProvider>
        )}
        <div className='flex items-center justify-between border-t px-3 py-2'>
          <span className='text-xs text-muted-foreground'>
            {values.length}개 선택
          </span>
          <Button
            variant='link'
            size='sm'
            className='h-auto p-0 text-xs'
            onClick={() => onValuesChange([])}
          >
            초기화
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
